import React, { useState, useEffect } from "react";
import { Table, Spinner, Input, Badge } from "reactstrap";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [paginate, setPaginate] = useState(10);
  const [count, setCount] = useState(0);
  const { AuthReducer: { auth } } = useSelector((state) => state);

  useEffect(() => {
    fetchNotifications();
  }, [page, paginate]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/api/admin/v1/notifications', {
        headers: { Authorization: `Bearer ${auth?.token}` },
        params: { page: page + 1, limit: paginate }
      });
      setNotifications(response.data.data || []);
      setCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`/api/admin/v1/notification/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        )
      );
      toast.success("Notification marked as read");
    } catch (error) {
      toast.error("Failed to mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.is_read);
      await Promise.all(
        unreadNotifications.map(n => 
          axios.put(`/api/admin/v1/notification/${n.id}/read`, {}, {
            headers: { Authorization: `Bearer ${auth?.token}` }
          })
        )
      );
      setNotifications(prev => prev.map(notif => ({ ...notif, is_read: true })));
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark all notifications as read");
    }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <div className="mb-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 text-white fs-4">All Notifications</h5>
            <button className="btn btn-primary" onClick={markAllAsRead}>
              Mark All as Read
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white px-3 py-4 rounded-2 my-4" style={{ boxShadow: "4px 4px 18px rgba(0,0,0,0.15)" }}>
        <Table hover responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>Title</th>
              <th>Message</th>
              <th>Date</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {notifications.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">No notifications found</td>
              </tr>
            ) : (
              notifications.map((notification) => (
                <tr key={notification.id} className={!notification.is_read ? 'table-warning' : ''}>
                  <td>
                    <Badge color={
                      notification.type === 'error' ? 'danger' :
                      notification.type === 'warning' ? 'warning' :
                      notification.type === 'success' ? 'success' : 'info'
                    }>
                      {notification.type}
                    </Badge>
                  </td>
                  <td>{notification.title}</td>
                  <td>{notification.message}</td>
                  <td>{new Date(notification.createdAt).toLocaleString()}</td>
                  <td>
                    {notification.is_read ? (
                      <Badge color="success">Read</Badge>
                    ) : (
                      <Badge color="warning">Unread</Badge>
                    )}
                  </td>
                  <td>
                    {!notification.is_read && (
                      <button 
                        className="btn btn-sm btn-primary"
                        onClick={() => markAsRead(notification.id)}
                      >
                        Mark as Read
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <div className="row align-items-center mt-3">
          <div className="col-md-6 d-flex">
            <div className="col-md-3">
              <Input
                type="select"
                value={paginate}
                onChange={(e) => setPaginate(parseInt(e.target.value))}
                size="sm"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </Input>
            </div>
            <div className="col-md-9 text-dark d-flex align-items-center ps-3">
              Showing {notifications.length} of {count} entries
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;